import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  getMessagePublicationsV2QueryOptions,
  type MessagePublication,
} from '@/services/getMessagePublicationsV2';
import type { PublicationShareEntry, PublicationShares } from '@/types/messages/base';
import { MESSAGE_DATA_TYPES } from '@/types/messages/discriminators';

const NO_PUBLICATIONS: MessagePublication[] = [];

const NO_ENTRIES: PublicationShareEntry[] = [];

interface MessageShares {
  /** The shared works' details, for the footer and the reference sheet. */
  publications: readonly MessagePublication[];
  /** Each work's number, where the text cites it and the statements it is
   * attached to, as the server numbered them. */
  shareEntries: readonly PublicationShareEntry[];
}

/**
 * The publications shared in a message and the server's numbering of them.
 *
 * The numbering comes from two copies of one list. The message's
 * `publicationShares` attribute arrives with the message, so chips show their
 * numbers before anything loads. The publications endpoint reads the same list
 * from the database once it loads, and wins from then on: Twilio refuses any
 * change to a closed conversation's messages, so there the attribute keeps
 * whatever it last held while the database moves on. Data kept on screen from
 * the previous fetch does not win, since the attribute changing is what
 * started the refetch and is the newer of the two.
 *
 * A reference card never fetches: the backend records the card's own link as
 * a share, so its list would only repeat the card the reader is looking at.
 *
 * A failed or withheld fetch leaves `publications` empty: the query retries
 * and then errors, and nothing here rethrows, so the bubble still renders.
 * Statement chips keep the attribute's numbers; the footer and inline chips
 * wait for the details they open. An error on a refetch also drops the list
 * kept on screen, since that only survives while a fetch is in flight.
 *
 * While a changed list refetches, the previous one stays on screen. Without
 * that, every share change empties the list for a moment, the footer jumps and
 * an open reference sheet loses the reference it shows. The previous data is
 * only reused for the same message, so one message's references can never
 * stand in for another's.
 */
export function useMessageShares(message: {
  type?: string;
  publicationShares?: PublicationShares;
}): MessageShares {
  const shares =
    message.type === MESSAGE_DATA_TYPES.REFERENCE ? undefined : message.publicationShares;

  const conversationMessageId = shares?.conversationMessageId ?? '';

  const { data, isPlaceholderData } = useQuery({
    ...getMessagePublicationsV2QueryOptions({
      conversationMessageId,
      lastAddedAt: shares?.lastAddedAt ?? '',
      updatedAt: shares?.updatedAt,
    }),
    enabled: shares !== undefined,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === conversationMessageId ? previous : undefined,
  });

  const publications = shares ? (data ?? NO_PUBLICATIONS) : NO_PUBLICATIONS;

  const attributeEntries = shares?.entries ?? NO_ENTRIES;

  const shareEntries = useMemo(
    () => (shares && data && !isPlaceholderData ? entriesFromPublications(data) : attributeEntries),
    [shares, data, isPlaceholderData, attributeEntries]
  );

  return { publications, shareEntries };
}

const entriesFromPublications = (
  publications: readonly MessagePublication[]
): PublicationShareEntry[] =>
  [...publications]
    .sort((first, second) => first.number - second.number)
    .map(({ shareId, number, anchors, statementIndices }) => ({
      shareId,
      number,
      anchors,
      statementIndices,
    }));
